import { ref } from "vue";

/**
 * Surviving a deploy with the tab already open.
 *
 * Every page is a lazy import, and Vite gives each chunk a content hash. When a
 * new version ships, the previous hashes stop existing — so a tab that loaded
 * the old index.html asks for a file the server no longer has the moment the
 * player navigates anywhere. Vue Router surfaces that as "Failed to fetch
 * dynamically imported module", which lands in whatever page the player was on
 * and reads like a bug in the game.
 *
 * It is not a bug, it is a new release, and the only cure is a full page load.
 * Where nothing is at stake this takes itself: the navigation the player asked
 * for happens as a real page load and they never learn there was a problem. On
 * the two pages holding a match in memory it asks first, because a match that
 * only exists in this tab must not be thrown away to save a click.
 */

/** True once a stale chunk has proved the tab is running an old release. */
export const updateAvailable = ref(false);

/** Where the player was trying to go when the stale chunk was found. */
const pendingTarget = ref<string | null>(null);

/** Guards against a reload loop when the new index.html is also unreachable. */
const RELOAD_KEY = "rasenschach.reloadedForUpdate";

/**
 * Vue Router reports this as a plain Error whose message differs per browser:
 * Chrome says "Failed to fetch dynamically imported module", Firefox "error
 * loading dynamically imported module", Safari "Importing a module script
 * failed". Matching the message is unpleasant but it is what there is.
 */
export function isStaleChunkError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? "");
  return /dynamically imported module|Importing a module script failed|error loading dynamically/i
    .test(message);
}

/**
 * Pages that hold a match only this tab knows about. Reloading one of these
 * silently would lose it, so they get asked rather than reloaded.
 */
function hasUnsavedMatch(fullPath: string): boolean {
  // The router hands over its fullPath: "/local?ai=sherlock&strength=hard" is
  // the usual shape of a local match, and comparing it to "/local" as a whole
  // reloaded exactly those matches without asking.
  const path = fullPath.split(/[?#]/, 1)[0] ?? "";
  return path === "/local" || path.startsWith("/game/");
}

function alreadyReloadedFor(target: string): boolean {
  try {
    return sessionStorage.getItem(RELOAD_KEY) === target;
  } catch {
    return false; // private mode: one extra reload beats never recovering
  }
}

function rememberReload(target: string): void {
  try {
    sessionStorage.setItem(RELOAD_KEY, target);
  } catch {
    /* nothing to do; the loop guard is best-effort */
  }
}

/** A navigation completed, so this release is evidently fine. */
export function clearUpdateState(): void {
  updateAvailable.value = false;
  pendingTarget.value = null;
  try {
    sessionStorage.removeItem(RELOAD_KEY);
  } catch {
    /* best-effort */
  }
}

/**
 * Handle a failed navigation. Returns true when it was a stale chunk and has
 * been dealt with, so the caller knows not to treat it as an ordinary error.
 */
export function handleNavigationError(
  error: unknown,
  target: string,
  currentPath: string,
): boolean {
  if (!isStaleChunkError(error)) return false;

  pendingTarget.value = target;
  updateAvailable.value = true;

  // A second failure for the same destination means reloading did not help;
  // stop, or the tab spins.
  if (alreadyReloadedFor(target)) return true;

  if (!hasUnsavedMatch(currentPath)) {
    rememberReload(target);
    window.location.assign(target);
  }
  return true;
}

/** The banner's button: go where the player was headed, as a fresh page. */
export function reloadForUpdate(): void {
  const target = pendingTarget.value;
  if (target) rememberReload(target);
  window.location.assign(target ?? window.location.pathname);
}
